import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Avatar } from "../../../components/Avatar/Avatar";
import SearchInput from "../../../components/MultiSelectSearchableDropdown/components/SearchInput";
import { visibleScrollbar } from "../../../utils/functions/get-visible-scrollbar";
import CheckIcon from "../../../assets/svg/check-icon";
// import { useGetAllUsersQuery } from "../../../redux/services/campaignApi";
import Button from "../../../components/Button/Button";
import AddIcon from "../../../assets/svg/add-icon";
import ChevronDownIcon from "../../../assets/svg/chevron-down-icon";
import { Tooltip } from "../../../components/Tooltip/Tooltip";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useGetAllAgentsQuery } from "../../../redux/services/userManagementApi";

export interface AgentUser {
  id: string;
  name: string;
  email?: string;
  phone_number?: string;
  country_code?: string;
  client_code?: string;
  role: string;
  status: string;
  profile_picture?: string | null;
}

export const AgentAssignmentDropdown = ({
  selectedAgentIds = [],
  onApply,
  isLoading = false,
  maxAgents = 4,
  title = "ASSIGN AGENTS",
  searchPlaceholder = "Search",
  onToggleScroll,
  isViewing = false,
  defaultSelectedAgents = [],
  agentsList = [],
  hideRole = false,
  subHeading,
  canSaveEmpty = false,
  triggerText = "Assign Agent",
  customTrigger,
  showReset = false,
  onReset = undefined,
  applyButtonText = undefined,
  triggerMaxWidth,
  triggerClassName,
}: {
  selectedAgentIds: string[];
  onApply: (ids: string[], users: AgentUser[]) => Promise<void>;
  isLoading?: boolean;
  maxAgents?: number;
  title?: string;
  searchPlaceholder?: string;
  onToggleScroll?: (lock: boolean) => void;
  isViewing?: boolean;
  defaultSelectedAgents?: AgentUser[];
  agentsList?: AgentUser[];
  hideRole?: boolean;
  subHeading?: string;
  canSaveEmpty?: boolean;
  triggerText?: string;
  customTrigger?: React.ReactNode;
  showReset?: boolean;
  onReset?: () => void;
  applyButtonText?: string;
  triggerMaxWidth?: string;
  triggerClassName?: string;
}) => {
  const userId = useSelector((state: RootState) => state.auth.user?._id);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelected, setTempSelected] = useState<Set<string>>(
    new Set(selectedAgentIds)
  );
  // Optimistically applied ids shown in the trigger while the parent prop
  // catches up (mutation loading + refetch).
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);
  const isDarkMode = useSelector(
    (state: RootState) => state.themeProvider.isDarkMode
  );

  const effectiveSelectedIds = pendingIds ?? selectedAgentIds;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [portalPos, setPortalPos] = useState<{ x: number; y: number } | null>(
    null
  );

  const { data: allUsersData, isFetching } = useGetAllAgentsQuery(undefined, {
    skip: agentsList?.length > 0,
  });

  const users: AgentUser[] = useMemo(() => {
    const agentIdSet = new Set<string>();
    const activeAgentList =
      agentsList?.length > 0 ? agentsList : allUsersData?.items;
    const apiUsers: AgentUser[] = (activeAgentList ?? [])
      .map(
        (u: {
          id?: string;
          _id?: string;
          name: string;
          role: string;
          status: string;
          profile_picture?: string | null;
          email?: string;
        }): AgentUser | null => {
          const agentId = u?.id ?? u?._id;
          if (!agentId || agentIdSet.has(agentId)) return null;
          agentIdSet.add(agentId);
          const isMe = agentId === userId;
          return {
            id: agentId,
            name: `${u?.name}${isMe ? " (Me)" : ""}`,
            role: u?.role,
            status: u?.status,
            profile_picture: u?.profile_picture ?? null,
            email: u?.email,
          };
        }
      )
      .filter((entry: AgentUser | null): entry is AgentUser => entry !== null);

    defaultSelectedAgents?.forEach((agent) => {
      if (agentIdSet.has(agent?.id)) return null;
      agentIdSet.add(agent?.id);
      const isMe = agent?.id === userId;
      apiUsers.push({
        id: agent?.id,
        name: `${agent?.name}${isMe ? " (Me)" : ""}`,
        role: agent?.role,
        status: agent?.status,
        profile_picture: agent?.profile_picture || null,
        email: agent?.email,
      });
    });

    return apiUsers;
  }, [allUsersData, defaultSelectedAgents, userId, agentsList]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter((u) => (u?.name || "").toLowerCase().includes(q));
  }, [users, searchQuery]);

  const selectedUsers = useMemo(
    () => users.filter((u) => tempSelected.has(u?.id || "")),
    [tempSelected, users]
  );

  const unselectedUsers = useMemo(
    () => filteredUsers.filter((u) => !tempSelected.has(u?.id || "")),
    [filteredUsers, tempSelected]
  );

  const roleDot = (role?: string) => {
    const roleLower = (role || "").toLowerCase();
    const color = roleLower.includes("super")
      ? "#22C55E"
      : roleLower.includes("admin")
        ? "#FACC15"
        : "#9CA3AF";
    return (
      <span
        className="inline-block w-2 h-2 rounded-full mr-1"
        style={{ backgroundColor: color }}
      />
    );
  };

  const toggleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen((p) => !p);
  }, []);

  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  const toggleStageScroll = useCallback(
    (lock: boolean) => {
      if (onToggleScroll) {
        onToggleScroll(lock);
        return;
      }
    },
    [onToggleScroll]
  );

  const computePortalPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Horizontal positioning with viewport clamping
    const panelWidth = 240; // width of the dropdown w-[240px]
    const viewportWidth = window.innerWidth;
    const margin = 8; // small side padding
    const spaceRight = viewportWidth - rect.right;
    const spaceLeft = rect.left;

    let x =
      spaceRight < panelWidth && spaceLeft > spaceRight
        ? rect.right + scrollLeft - panelWidth
        : rect.left + scrollLeft;

    const minX = scrollLeft + margin;
    const maxX = scrollLeft + viewportWidth - panelWidth - margin;
    if (isViewing) {
      x = Math.max(minX, Math.min(x, maxX)) - 200;
    } else {
      x = Math.max(minX, Math.min(x, maxX));
    }

    const viewportHeight = window.innerHeight;
    const dropdownHeight = dropdownRef.current?.offsetHeight || 300;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const y =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow
        ? rect.top + scrollTop - dropdownHeight
        : rect.bottom + scrollTop + 5;

    setPortalPos({ x, y });
  }, []);

  // Reset the working selection only when the dropdown opens, not on every
  // parent re-render (selectedAgentIds is a new array each render). Otherwise
  // an in-flight parent re-render (e.g. mutation loading) would wipe the user's
  // in-progress selection back to the stale prop value.
  useEffect(() => {
    if (isOpen) {
      setTempSelected(new Set(selectedAgentIds));
      setPendingIds(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      setTimeout(() => searchInputRef.current?.focus(), 100);
      toggleStageScroll(true);
      computePortalPosition();
      window.addEventListener("resize", computePortalPosition);
      window.addEventListener("scroll", computePortalPosition, true);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
      setSearchQuery("");
      toggleStageScroll(false);
      window.removeEventListener("resize", computePortalPosition);
      window.removeEventListener("scroll", computePortalPosition, true);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, handleOutsideClick, toggleStageScroll, computePortalPosition]);

  // After the portal content mounts, refine position using actual measured height
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      computePortalPosition();
    }
  }, [isOpen, computePortalPosition]);

  // Clear optimistic ids once the parent prop reflects the applied selection.
  useEffect(() => {
    if (!pendingIds) return;
    const current = [...selectedAgentIds].sort();
    const applied = [...pendingIds].sort();
    if (
      current.length === applied.length &&
      current.every((id, index) => id === applied[index])
    ) {
      setPendingIds(null);
    }
  }, [selectedAgentIds, pendingIds]);

  const handleToggleUser = (id: string) => {
    const newTempSelected = new Set(tempSelected);
    if (newTempSelected.has(id)) {
      newTempSelected.delete(id);
    } else {
      if (maxAgents === 1) {
        newTempSelected.clear();
      }
      newTempSelected.add(id);
    }
    setTempSelected(newTempSelected);
  };

  const handleApply = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const appliedIds = Array.from(tempSelected);
    setPendingIds(appliedIds);
    onApply(appliedIds, selectedUsers as AgentUser[]).finally(() => {
      setIsOpen(false);
    });
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTempSelected(new Set(selectedAgentIds));
    setIsOpen(false);
  };

  const popupSubText =
    subHeading ?? `Assign this lead to up to ${maxAgents} agents.`;

  const trigger = useMemo(() => {
    if (customTrigger) return customTrigger;

    const defaultTrigger = (
      <Button
        Icon={AddIcon}
        iconLeft
        secondaryButtonContainerClass="!gap-[2px]"
        iconClass="text-verba-blue dark:text-verba-blue-dk w-[18px] h-[18px]"
        innerTextClass="text-md font-medium text-verba-blue dark:text-verba-blue-dk hover:underline"
        innerText={triggerText}
        buttonContainerClass={`w-fit min-h-[26px] min-w-[] py-1 px-3 rounded-[6px] border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors ${triggerClassName}`}
      />
    );
    if (effectiveSelectedIds.length > 0) {
      const selectedUsers = users.filter((u) =>
        effectiveSelectedIds.includes(u?.id || "")
      );
      if (!selectedUsers.length) return defaultTrigger;
      return (
        <Button
          secondaryButtonContainerClass="w-full !p-0"
          innerText={
            <div className="w-full flex items-center gap-2">
              {" "}
              <Avatar
                text={selectedUsers[0]?.name || ""}
                maxChars={1}
                size={18}
              />
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="max-w-[120px] text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                  {selectedUsers[0]?.name}
                </div>

                <div className="flex items-center gap-0.5 text-xxs text-dark-gray dark:text-dark-gray-dk">
                  {!hideRole && selectedUsers[0]?.role && (
                    <>
                      {roleDot(selectedUsers[0]?.role)}
                      <span className="max-w-[80px] truncate capitalize">
                        {selectedUsers[0]?.role}
                      </span>
                    </>
                  )}
                  {selectedUsers.length > 1 && (
                    <Tooltip
                      content={
                        <div className="flex flex-col gap-1 items-start justify-center">
                          {selectedUsers.map((u) => (
                            <div
                              key={u?.id}
                              className="flex items-center gap-2"
                            >
                              <div className="flex items-center gap-1">
                                <Avatar
                                  text={u?.name || ""}
                                  maxChars={1}
                                  size={18}
                                />
                                <p className="font-medium text-sm text-pure-white dark:text-pure-white-dk max-w-[200px] truncate">
                                  {u?.name}
                                </p>
                              </div>
                              <div className="w-px h-[10px] bg-gray-175 dark:bg-gray-175-dk" />
                              <div className="flex items-center gap-0.5">
                                {roleDot(selectedUsers[0]?.role)}
                                <span className="max-w-[55px] truncate capitalize text-pure-white dark:text-pure-white-dk text-xxs font-medium">
                                  {selectedUsers[0]?.role}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <div className="text-sm ml-0.5 text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-sm px-1 py-0.5 whitespace-nowrap">
                        +{selectedUsers.length - 1}
                      </div>
                    </Tooltip>
                  )}
                  <ChevronDownIcon className="ml-0.5w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk" />
                </div>
              </div>
            </div>
          }
          innerTextClass="w-full"
          buttonContainerClass={`${triggerMaxWidth || "max-w-[280px]"} py-1 h-[26px] px-1.5 border border-border-gray dark:border-border-gray-dk rounded-[6px] px-1.5 py-2`}
        />
      );
    } else {
      return defaultTrigger;
    }
  }, [effectiveSelectedIds, users, hideRole, customTrigger, triggerMaxWidth]);

  return (
    <div className={`relative inline-block`}>
      <div
        ref={triggerRef}
        onClick={toggleOpen}
        className="cursor-pointer select-none"
      >
        {trigger}
      </div>

      {isOpen &&
        portalPos &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed z-1000000 w-[240px] max-h-[292px] overflow-hidden backdrop-blur-[10px]
            bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk 
            rounded-[8px] shadow-popper-blur-effect flex flex-col`}
            style={{ left: portalPos.x, top: portalPos.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-data dark:text-data-dk uppercase">
                  {title}
                </div>
                {showReset && onReset && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReset();
                      setIsOpen(false);
                    }}
                    className="text-sm font-medium text-verba-blue dark:text-verba-blue-dk hover:underline cursor-pointer"
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
                {popupSubText}
              </div>
            </div>

            <SearchInput
              searchQuery={searchQuery}
              searchPlaceholder={searchPlaceholder}
              searchInputRef={searchInputRef}
              onSearchQueryChange={setSearchQuery}
              showSearch={true}
            />

            <div
              className={`flex-1 overflow-y-auto ${visibleScrollbar(
                "[&::-webkit-scrollbar]:w-[4px]"
              )}`}
            >
              {isFetching ? (
                <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  Loading users...
                </div>
              ) : (
                <>
                  {selectedUsers.length > 0 && (
                    <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                      {selectedUsers.map((u) => (
                        <Button
                          key={u?.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleUser(u?.id || "");
                          }}
                          secondaryButtonContainerClass="w-full !p-0"
                          innerText={
                            <div className={`w-full flex items-center gap-2`}>
                              {" "}
                              <Avatar
                                text={u?.name || ""}
                                maxChars={1}
                                size={18}
                              />
                              <div className="flex-1 flex items-center justify-between min-w-0">
                                <div className="max-w-[100px] text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                                  {u?.name}
                                </div>
                                {!hideRole && u?.role && (
                                  <div className="flex items-center gap-1 text-xxs text-dark-gray dark:text-dark-gray-dk">
                                    {roleDot(u?.role)}
                                    <span className="max-w-[65px] truncate capitalize">
                                      {u?.role}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div
                                className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] ${
                                  tempSelected.has(u?.id || "")
                                    ? "bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk"
                                    : "border-gray-100 dark:border-gray-100-dk"
                                }`}
                              >
                                {tempSelected.has(u?.id || "") && (
                                  <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                                )}
                              </div>
                            </div>
                          }
                          innerTextClass="w-full"
                          buttonContainerClass={`w-full py-[10px] px-3 ${
                            tempSelected.has(u?.id || "")
                              ? "bg-card-highlight-2 dark:bg-card-highlight-2-dk "
                              : " hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                          }`}
                        />
                      ))}
                    </div>
                  )}

                  <div className="px-[12px] py-[8px] text-xxs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-y border-border-gray dark:border-border-gray-dk">
                    ALL AGENTS
                  </div>

                  {unselectedUsers.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                      {searchQuery.trim()
                        ? "No users found"
                        : "No users available"}
                    </div>
                  ) : (
                    <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                      {unselectedUsers.map((u) => {
                        const isSelected = tempSelected.has(u?.id || "");
                        // if maxAgents is 0, then always allow all agents to be toggled
                        const canAddMore =
                          maxAgents === 0 ||
                          (maxAgents > 0 && tempSelected.size < maxAgents) ||
                          isSelected;
                        return (
                          <Button
                            key={u?.id}
                            disabled={!canAddMore}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleUser(u?.id || "");
                            }}
                            secondaryButtonContainerClass="w-full !p-0"
                            innerText={
                              <div className={`w-full flex items-center gap-2`}>
                                {" "}
                                <Avatar
                                  text={u?.name || ""}
                                  maxChars={1}
                                  size={18}
                                />
                                <div className="flex-1 flex items-center justify-between min-w-0">
                                  <div className="max-w-[100px] text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                                    {u?.name}
                                  </div>
                                  {!hideRole && u?.role && (
                                    <div className="flex items-center gap-1 text-xxs text-dark-gray dark:text-dark-gray-dk">
                                      {roleDot(u?.role)}
                                      <span className="max-w-[65px] truncate capitalize">
                                        {u?.role}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] ${
                                    isSelected
                                      ? "bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk"
                                      : "border-gray-100 dark:border-gray-100-dk"
                                  }`}
                                >
                                  {isSelected && (
                                    <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                                  )}
                                </div>
                              </div>
                            }
                            innerTextClass="w-full"
                            buttonContainerClass={`w-full py-[10px] px-3 ${
                              isSelected
                                ? "bg-card-highlight-2 dark:bg-card-highlight-2-dk "
                                : " hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                            } ${
                              !canAddMore ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-[12px] py-[10px]">
              <Button
                innerText="Cancel"
                innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-semibold"
                buttonContainerClass="min-w-[104px] w-fit px-2 py-1.5 rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                onClick={handleCancel}
              />

              <Button
                onClick={handleApply}
                isLoading={isLoading}
                disabled={
                  (tempSelected.size === selectedAgentIds.length &&
                    Array.from(tempSelected).every((id) =>
                      selectedAgentIds.includes(id)
                    )) ||
                  (selectedAgentIds.length > 0 &&
                    tempSelected.size === 0 &&
                    !canSaveEmpty)
                }
                innerText={applyButtonText ?? `Assign (${tempSelected.size})`}
                innerTextClass={`text-md font-semibold ${
                  (tempSelected.size === selectedAgentIds.length &&
                    Array.from(tempSelected).every((id) =>
                      selectedAgentIds.includes(id)
                    )) ||
                  (selectedAgentIds.length > 0 &&
                    tempSelected.size === 0 &&
                    !canSaveEmpty)
                    ? " text-pure-white dark:text-pure-white-dk  "
                    : " text-pure-white dark:text-pure-white-dk  group-hover:text-pure-white dark:group-hover:text-pure-white! "
                }`}
                buttonContainerClass={`min-w-[104px] group rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk py-1.5 px-2 disabled:opacity-25 disabled:cursor-not-allowed! ${
                  (tempSelected.size === selectedAgentIds.length &&
                    Array.from(tempSelected).every((id) =>
                      selectedAgentIds.includes(id)
                    )) ||
                  (selectedAgentIds.length > 0 &&
                    tempSelected.size === 0 &&
                    !canSaveEmpty)
                    ? ""
                    : " hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:border-verba-blue hover:dark:border-verba-blue-dk"
                } `}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AgentAssignmentDropdown;
